/**
 * KPI investigations — API + shared label maps.
 *
 * Backend: hickory-ui/backend/app/api/endpoints/kpi_investigations.py.
 * Consumed by the cell-menu dialog (create) and the /kpi-investigations page.
 */
import { fetchApi } from "@/lib/api";

export type KpiInvestigationDirection = "high" | "low";

/** The dialog's category options (Kory's flow adds data_platform). */
export type KpiInvestigationCreateCategory =
  | "sop"
  | "definition_disagreement"
  | "code_implementation"
  | "performance_deviation"
  | "other";

export type KpiInvestigationCategory = KpiInvestigationCreateCategory | "data_platform";

export type KpiInvestigationStatus = "open" | "resolved";

export interface KpiInvestigation {
  id: string;
  kpi_slug: string | null;
  metric_name: string | null;
  tenant_id: string | null;
  brand_name: string | null;
  direction: KpiInvestigationDirection | null;
  category: KpiInvestigationCategory;
  detail: string | null;
  value_summary: string | null;
  status: KpiInvestigationStatus;
  origin: "cell_menu" | "kory" | "todo_conversion";
  opened_at: string;
  resolved_at: string | null;
  created_by_user_id: string | null;
  created_by_name: string | null;
  assignee_user_id: string | null;
  assignee_name: string | null;
  assigned_via: string | null;
}

export interface KpiInvestigationNote {
  id: string;
  body: string;
  created_at: string;
  author_user_id: string;
  author_name: string | null;
}

export interface KpiInvestigationDetail extends KpiInvestigation {
  notes: KpiInvestigationNote[];
}

export interface KpiInvestigationCreateInput {
  kpi_slug: string;
  category: KpiInvestigationCreateCategory;
  direction: KpiInvestigationDirection;
  metric_name?: string;
  brand?: string;
  detail?: string;
  value_summary?: string;
  page_key?: string;
}

/** Dialog option copy, in display order. */
export const CATEGORY_OPTIONS: { value: KpiInvestigationCreateCategory; label: string; description: string }[] = [
  {
    value: "sop",
    label: "Not being completed right at the SOP level",
    description: "People aren't operating correctly at the location (data entry, workflow adherence).",
  },
  {
    value: "definition_disagreement",
    label: "The calculation definition seems wrong",
    description: "What this metric should count — its business meaning — is off or debatable.",
  },
  {
    value: "code_implementation",
    label: "The calculation seems buggy",
    description: "The definition is fine but the computed number looks wrong.",
  },
  {
    value: "performance_deviation",
    label: "Concerning deviation from expected performance",
    description: "The number is real — the business result itself needs chasing.",
  },
  { value: "other", label: "Other", description: "Something else about this number needs a look." },
];

/** Short labels for list/badge rendering (covers Kory's data_platform too). */
/** Business dates render in Eastern, independent of the reader's browser zone. */
export function formatBusinessTimestamp(iso: string, withTime = false): string {
  const date = new Date(iso);
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
  if (!withTime) return day;
  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  return `${day} at ${time}`;
}

export const CATEGORY_LABELS: Record<KpiInvestigationCategory, string> = {
  sop: "SOP execution",
  definition_disagreement: "Definition",
  code_implementation: "Calculation bug",
  data_platform: "Source data",
  performance_deviation: "Performance",
  other: "Other",
};

export function createKpiInvestigation(input: KpiInvestigationCreateInput): Promise<KpiInvestigation> {
  return fetchApi("/kpi-investigations", { method: "POST", body: JSON.stringify(input) });
}

export async function getKpiInvestigations(status?: KpiInvestigationStatus): Promise<KpiInvestigation[]> {
  const qs = status ? `?status_filter=${status}` : "";
  const res: { investigations: KpiInvestigation[] } = await fetchApi(`/kpi-investigations${qs}`);
  return res.investigations;
}

export function getKpiInvestigation(id: string): Promise<KpiInvestigationDetail> {
  return fetchApi(`/kpi-investigations/${id}`);
}

export function addKpiInvestigationNote(id: string, body: string): Promise<KpiInvestigationNote> {
  return fetchApi(`/kpi-investigations/${id}/notes`, { method: "POST", body: JSON.stringify({ body }) });
}

export function updateKpiInvestigationStatus(id: string, status: KpiInvestigationStatus): Promise<KpiInvestigation> {
  return fetchApi(`/kpi-investigations/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
}

/** ``assigneeUserId: null`` clears the assignee. Manage capability only. */
export function assignKpiInvestigation(id: string, assigneeUserId: string | null): Promise<KpiInvestigation> {
  return fetchApi(`/kpi-investigations/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ assignee_user_id: assigneeUserId }),
  });
}
